import { z } from 'zod';
import {
  CourseTypes,
  DurationTypes,
  ModalityTypes,
  PriceTypes,
} from '@/models/catalog/course';
import { PeriodTypes } from '@/models/catalog/offer';

const mainValidationSchema = z.object({
  amount: z.coerce
    .number({ message: 'Valor obrigatório' })
    .min(1)
    .positive({ message: 'Valor deve ser maior que zero' }),
  course: z
    .string({ message: 'Curso obrigatório' })
    .min(1, 'Curso obrigatório'),
  course_type: z.enum(CourseTypes, { message: 'Categoria obrigatória' }),
  institution: z
    .string({ message: 'Instituição de ensino obrigatória' })
    .min(1, 'Instituição de ensino obrigatória'),
  link: z.url('URL inválida').optional().nullable(),
  request_reason: z.string().optional().nullable(),
  sale_type: z.enum(PriceTypes, {
    message: 'Tipo de pagamento obrigatório',
  }),
  workload: z
    .union([
      z.null(),
      z.coerce.number().int().positive('Valor deve ser maior que zero'),
    ])
    .optional()
    .nullable(),
});

const modalitySchema = z
  .object({
    city: z.string(),
    campus: z.string(),
    period: z.union([z.enum(PeriodTypes), z.literal('')]).nullable(),
    state: z.string(),
    modality: z.enum(ModalityTypes, { message: 'Modalidade obrigatória' }),
  })
  .partial()
  .superRefine(({ city, campus, period, state, modality }, ctx) => {
    if (!modality) {
      ctx.addIssue({
        code: 'custom',
        message: 'Modalidade obrigatória',
        path: ['modality'],
      });
    }
    if (
      modality &&
      [ModalityTypes.Hybrid, ModalityTypes.InPerson].includes(modality)
    ) {
      if (!state) {
        ctx.addIssue({
          code: 'custom',
          message: 'Estado obrigatório',
          path: ['state'],
        });
      }
      if (!city) {
        ctx.addIssue({
          code: 'custom',
          message: 'Cidade obrigatória',
          path: ['city'],
        });
      }
      if (!campus) {
        ctx.addIssue({
          code: 'custom',
          message: 'Polo obrigatório',
          path: ['campus'],
        });
      }
      if (!period) {
        ctx.addIssue({
          code: 'custom',
          message: 'Turno obrigatório',
          path: ['period'],
        });
      }
    }
  });

// zod has a limitation with dependent fields, so it's best to have them in separate schema
// more about this: https://github.com/colinhacks/zod/issues/479#issuecomment-1536233005
const durationSchema = z
  .object({
    disable_duration: z.boolean().nullable(),
    duration: z.union([
      z.null(),
      z.coerce.number().int().positive('Valor deve ser maior que zero'),
    ]),
    duration_type: z.union([z.enum(DurationTypes), z.literal('')]),
  })
  .partial()
  .superRefine(({ disable_duration, duration, duration_type }, ctx) => {
    if (!disable_duration) {
      if (duration === null || duration === undefined) {
        ctx.addIssue({
          code: 'custom',
          message: 'Valor obrigatório',
          path: ['duration'],
        });
      }
      if (!duration_type) {
        return ctx.addIssue({
          code: 'custom',
          message: 'Tipo obrigatório',
          path: ['duration_type'],
        });
      }
    }
  });

// z.intersection does not work to merge schemas here because
// we're using .refine/.superRefine which outputs ZodEffects instead of ZodObject.
// This is poorly documented in the zod documentation.
// As a workaround, we're using .and to merge the schemas.
// More about this:
// https://github.com/colinhacks/zod/issues/2474
// https://github.com/colinhacks/zod/issues/1147
export const externalCourseFormSchema = mainValidationSchema
  .and(modalitySchema)
  .and(durationSchema);

export type ExternalCourseFormSchema = z.infer<typeof externalCourseFormSchema>;
